import * as yup from 'yup';

/**
 * E49 · Motivation — the both-ends input codecs of the seven mutations (plan 03 §2.1 #3): the phone (before it saves), the portal and — as
 * the shield's inputRule — the API run the SAME schema. SHAPE only; the business rules (at most three open changes, a change that is not
 * the caller's or is closed, a ruler outside 0–10, all four boxes empty, no change in the plan) are the deciders', where they are binding
 * and answer with the board's coded refusals in the person's language. `performer` is ABSENT on purpose: the caller is the performer. The
 * lists mirror `@cc/domain` `motivation/constants.ts` (literals here so the schema package stays free of the domain package).
 */
export const MI_RULER_VALUES = [
  'importance',
  'confidence',
  'readiness',
] as const;
export const MI_CLOSE_OUTCOME_VALUES = [
  'done',
  'decidedNotTo',
  'other',
] as const;
export const MI_TALK_KIND_VALUES = [
  'desire',
  'ability',
  'reason',
  'need',
  'commitment',
  'takingSteps',
] as const;

const uuidV7 = yup
  .string()
  .trim()
  .matches(
    /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
  )
  .required();

const userRef = yup
  .string()
  .trim()
  .matches(/^users\/[A-Za-z0-9_-]{1,64}$/);

const idRef = yup.string().trim().min(1).max(128);

const words = (max: number) => yup.string().max(max).nullable();

/** One of the person's lines (a blank line is dropped by the decider). */
const lines = yup
  .array()
  .of(yup.string().max(1000).defined())
  .max(20)
  .nullable();

export const setChangeGoalInputSchema = yup.object({
  subject: userRef.nullable(),
  goalId: idRef.nullable(),
  // a blank label is the decider's coded refusal (MI_GOAL_LABEL_REQUIRED)
  label: yup.string().max(160).required(),
  linkedValues: yup
    .array()
    .of(yup.string().max(200).defined())
    .max(10)
    .nullable(),
});

export const rateChangeInputSchema = yup.object({
  ratingId: uuidV7,
  goalId: idRef.required(),
  // whole numbers here; whether they are 0–10 is the decider's (MI_RULER_RANGE)
  importance: yup.number().integer().required(),
  confidence: yup.number().integer().required(),
  readiness: yup.number().integer().required(),
  // `null` in the list: on yup 0.32 `.nullable()` does not let a null through a `oneOf` (none when every ruler is 0 or 10)
  focusedRuler: yup
    .string()
    .oneOf([...MI_RULER_VALUES, null])
    .nullable(),
  whyNotLower: words(1000),
  whatWouldRaise: words(1000),
});

export const writeDecisionalBalanceInputSchema = yup.object({
  goalId: idRef.required(),
  prosOfStaying: lines,
  consOfStaying: lines,
  consOfChange: lines,
  prosOfChange: lines,
});

export const writeChangePlanInputSchema = yup.object({
  goalId: idRef.required(),
  // a blank change is the decider's coded refusal (MI_PLAN_CHANGE_REQUIRED)
  changes: yup.string().max(1000).required(),
  reasons: lines,
  planSteps: yup
    .array()
    .of(
      yup
        .object({ step: yup.string().max(1000).required(), when: words(200) })
        .required(),
    )
    .max(20)
    .nullable(),
  helpers: lines,
  obstacles: yup
    .array()
    .of(
      yup
        .object({
          obstacle: yup.string().max(1000).required(),
          response: words(1000),
        })
        .required(),
    )
    .max(20)
    .nullable(),
  signsOfSuccess: words(1000),
});

export const noteChangeTalkInputSchema = yup.object({
  noteId: uuidV7,
  goalId: idRef.required(),
  // a blank line is the decider's coded refusal (MI_TALK_EMPTY)
  talk: yup.string().max(1000).required(),
  kind: yup
    .string()
    .oneOf([...MI_TALK_KIND_VALUES, null])
    .nullable(),
  noticedAt: yup.date().nullable(),
});

export const closeChangeGoalInputSchema = yup.object({
  goalId: idRef.required(),
  outcome: yup
    .string()
    .oneOf([...MI_CLOSE_OUTCOME_VALUES])
    .required(),
  note: words(1000),
});

export const reopenChangeGoalInputSchema = yup.object({
  goalId: idRef.required(),
});

/** The mutation ARGUMENTS — what the shield's inputRules validate. */
export const setChangeGoalArgsSchema = yup.object({
  input: setChangeGoalInputSchema.required(),
});
export const rateChangeArgsSchema = yup.object({
  input: rateChangeInputSchema.required(),
});
export const writeDecisionalBalanceArgsSchema = yup.object({
  input: writeDecisionalBalanceInputSchema.required(),
});
export const writeChangePlanArgsSchema = yup.object({
  input: writeChangePlanInputSchema.required(),
});
export const noteChangeTalkArgsSchema = yup.object({
  input: noteChangeTalkInputSchema.required(),
});
export const closeChangeGoalArgsSchema = yup.object({
  input: closeChangeGoalInputSchema.required(),
});
export const reopenChangeGoalArgsSchema = yup.object({
  input: reopenChangeGoalInputSchema.required(),
});

// Typed loosely on purpose (TS7056 — see diaryCard.ts): the shape is the Yup schemas above.
export const motivationValidators: Record<
  | 'setChangeGoalInputSchema'
  | 'rateChangeInputSchema'
  | 'writeDecisionalBalanceInputSchema'
  | 'writeChangePlanInputSchema'
  | 'noteChangeTalkInputSchema'
  | 'closeChangeGoalInputSchema'
  | 'reopenChangeGoalInputSchema'
  | 'setChangeGoalArgsSchema'
  | 'rateChangeArgsSchema'
  | 'writeDecisionalBalanceArgsSchema'
  | 'writeChangePlanArgsSchema'
  | 'noteChangeTalkArgsSchema'
  | 'closeChangeGoalArgsSchema'
  | 'reopenChangeGoalArgsSchema',
  yup.AnySchema
> = {
  setChangeGoalInputSchema,
  rateChangeInputSchema,
  writeDecisionalBalanceInputSchema,
  writeChangePlanInputSchema,
  noteChangeTalkInputSchema,
  closeChangeGoalInputSchema,
  reopenChangeGoalInputSchema,
  setChangeGoalArgsSchema,
  rateChangeArgsSchema,
  writeDecisionalBalanceArgsSchema,
  writeChangePlanArgsSchema,
  noteChangeTalkArgsSchema,
  closeChangeGoalArgsSchema,
  reopenChangeGoalArgsSchema,
};
